import { useState } from 'react'
import styled from 'styled-components'
import { parse } from 'date-fns'
import { DateRangePicker } from '../../../../../../generic/ui/DatePicker'
import { RadioSlider } from '../../../../../../generic/ui/Toggles/RadioSlider'
import type { RadioSliderItem } from '../../../../../../generic/ui/Toggles/RadioSlider'
import type { IReceiptReportParams } from '../../../../../../modules/API/Reciept/models/requests'
import ClientSelector from '../../../../../../generic/ui/Selectors/ClientSelector'
import SellerSelector from '../../../../../../generic/ui/Selectors/SellerSelector'
import PaymentTypeSelector, { PaymentTypeFilterValue } from '../../../../../../generic/ui/Selectors/PaymentTypeSelector'
import { AccentButton } from '../../../../../../generic/ui/Button/AccentButton'
import { FormShell, FormGrid, FormField, FormFooter } from '../../../../../../generic/ui/FormLayout'
import { resolvePartialEnd } from '../../../procedures/Q4/helpers/dateRange'
import { ReceiptsReportFilterFormProps } from '../hooks/useReceiptsReport'

type Props = ReceiptsReportFilterFormProps & {
  closeFunc?: () => void
}

const parseYmd = (ymd: string): Date => parse(ymd, 'yyyyMMdd', new Date())

const DATE_TYPE_TABS: RadioSliderItem[] = [
  { id: '1', label: 'רישום' },
  { id: '2', label: 'ערך' },
]

/** Receipts report filters — the fields of the v2/receipt/lines request; 0 / '' — all. */
const ReceiptsReportFilterForm = ({ initialFilters, clients, sellers, handleSave }: Props) => {
  const [startDate, setStartDate] = useState<string>(initialFilters.DateFrom)
  const [endDate, setEndDate] = useState<string>(initialFilters.DateTo)
  const [dateType, setDateType] = useState<IReceiptReportParams['DateType']>(initialFilters.DateType)
  const [clientID, setClientID] = useState<number>(initialFilters.CustomerID)
  const [sellerID, setSellerID] = useState<number>(initialFilters.SellerID)
  const [paymentType, setPaymentType] = useState<PaymentTypeFilterValue>(initialFilters.PaymentType)

  // Lists may arrive after the form opens — the choice is kept by id and resolved on render.
  const selectedClient = clients?.find((c) => c.ClientID === clientID) ?? null
  const selectedSeller = sellers?.find((s) => s.SellerID === sellerID) ?? null

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const partialEnd = resolvePartialEnd(dates)
    if (partialEnd) setEndDate(partialEnd)
  }

  const handleSubmit = () =>
    handleSave({
      DateFrom: startDate,
      DateTo: endDate,
      DateType: dateType,
      CustomerID: clientID,
      SellerID: sellerID,
      PaymentType: paymentType,
    })

  return (
    <TallFormShell>
      <FormGrid>
        <DateRow>
          <FormField label='סוג תאריך'>
            <SliderSlot>
              <RadioSlider
                items={DATE_TYPE_TABS}
                value={String(dateType)}
                onChange={(id) => setDateType(id === '2' ? 2 : 1)}
                ariaLabel='סוג תאריך'
              />
            </SliderSlot>
          </FormField>

          <FormField label='תאריכים'>
            <DateRangePicker
              selected={[parseYmd(initialFilters.DateFrom), parseYmd(initialFilters.DateTo)]}
              setDate={setStartDate}
              setDateEnd={setEndDate}
              onChange={handleDateChange}
            />
          </FormField>
        </DateRow>

        <FormField label='לקוח' fullWidth>
          <ClientSelector
            clients={clients ?? []}
            value={selectedClient}
            onSelect={(client) => setClientID(client?.ClientID ?? 0)}
            allowClear
          />
        </FormField>

        <FormField label='משווק'>
          <SellerSelector
            sellers={sellers ?? []}
            value={selectedSeller}
            onSelect={(seller) => setSellerID(seller?.SellerID ?? 0)}
            allowClear
          />
        </FormField>

        <FormField label='אמצעי תשלום'>
          <PaymentTypeSelector value={paymentType} onSelect={setPaymentType} />
        </FormField>
      </FormGrid>
      <Footer>
        <AccentButton type='button' onClick={handleSubmit}>
          {'חיפוש'}
        </AccentButton>
      </Footer>
    </TallFormShell>
  )
}

export default ReceiptsReportFilterForm

/* Without a minimum height the open lists of the bottom row hit the modal edge. */
const TallFormShell = styled(FormShell)`
  min-height: 24rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 22rem;
  }
`

/** Date type ahead of the dates on one row: the switch keeps its own width, the dates take the rest. */
const DateRow = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1rem 1.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.625rem 0.5rem;
  }
`

const SliderSlot = styled.div`
  display: flex;
`

/** Pinned to the bottom of the form even when the fields are short; sticky while scrolling. */
const Footer = styled(FormFooter)`
  margin-top: auto;
`
